import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { financeApi, ExpenditureByProjectRow, ExpenditureByAccountRow } from '../../api/finance';
import { DataTable } from '../../components/DataTable';

function money(n: string | number) {
  return Number(n).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 });
}

/**
 * The real donor-style Expenditure Report - genuine posted spend
 * only, grouped two ways (by Project/Donor/Grant, and by Account),
 * both computed from the exact same accounting engine every other
 * Finance report reads from - never a separate data source.
 */
export function ReportsPage() {
  const today = new Date().toISOString().slice(0, 10);
  const yearStart = `${new Date().getFullYear()}-01-01`;
  const [startDate, setStartDate] = useState(yearStart);
  const [endDate, setEndDate] = useState(today);
  const [applied, setApplied] = useState<{ startDate: string; endDate: string } | null>(null);

  const byProjectQuery = useQuery({
    queryKey: ['expenditure-by-project', applied],
    queryFn: () => financeApi.getExpenditureByProject(applied!.startDate, applied!.endDate),
    enabled: !!applied,
  });
  const byAccountQuery = useQuery({
    queryKey: ['expenditure-by-account', applied],
    queryFn: () => financeApi.getExpenditureByAccount(applied!.startDate, applied!.endDate),
    enabled: !!applied,
  });

  const byProjectRows = byProjectQuery.data?.data ?? [];
  const byAccountRows = byAccountQuery.data?.data ?? [];
  const projectTotal = byProjectRows.reduce((sum, r) => sum + Number(r.total_expenditure), 0);
  const accountTotal = byAccountRows.reduce((sum, r) => sum + Number(r.total_expenditure), 0);

  return (
    <div className="page">
      <div className="page-header">
        <h1>Reports</h1>
        {applied && <a href={financeApi.expenditureReportPdfUrl(applied.startDate, applied.endDate)} target="_blank" rel="noreferrer">Download PDF</a>}
      </div>
      <p className="muted">Genuine posted expenditure only, for the selected date range - the standard donor-reporting breakdown.</p>

      <section className="card">
        <label>Start Date<input type="date" value={startDate} onChange={(e) => setStartDate(e.target.value)} /></label>
        <label>End Date<input type="date" value={endDate} onChange={(e) => setEndDate(e.target.value)} /></label>
        <div className="form-actions"><button type="button" onClick={() => setApplied({ startDate, endDate })}>Run Report</button></div>
      </section>

      {applied && (
        <>
          <section className="card">
            <h2>Expenditure by Project, Donor &amp; Grant</h2>
            <DataTable<ExpenditureByProjectRow>
              rows={byProjectRows}
              rowKey={(r) => `${r.project_name}-${r.donor_name}-${r.grant_reference}`}
              emptyMessage="No posted expenditure in this range."
              columns={[
                { header: 'Project', render: (r) => r.project_name ?? '(No project)' },
                { header: 'Donor', render: (r) => r.donor_name ?? '-' },
                { header: 'Grant', render: (r) => r.grant_reference ?? '-' },
                { header: 'Expenditure', render: (r) => money(r.total_expenditure) },
              ]}
            />
            {byProjectRows.length > 0 && <p style={{ marginTop: '1em' }}><strong>Total: {money(projectTotal)}</strong></p>}
          </section>

          <section className="card">
            <h2>Expenditure by Account</h2>
            <DataTable<ExpenditureByAccountRow>
              rows={byAccountRows}
              rowKey={(r) => r.account_code}
              emptyMessage="No posted expenditure in this range."
              columns={[
                { header: 'Code', render: (r) => r.account_code },
                { header: 'Account', render: (r) => r.account_name },
                { header: 'Expenditure', render: (r) => money(r.total_expenditure) },
              ]}
            />
            {byAccountRows.length > 0 && <p style={{ marginTop: '1em' }}><strong>Total: {money(accountTotal)}</strong></p>}
          </section>
        </>
      )}
    </div>
  );
}
